/** Ряд окна «График статьи» (сводка → иконка у группы/статьи P&L): текущий период против
 *  периода сравнения. Чистые функции, без React.
 *
 *  Ось — ВСЕ корзины периода из `buckets_cur`/`buckets_cmp` (бэк, `mart.buckets_of`); корзина
 *  без начислений = 0, а не дырка. Сравнение выравнивается ПО ПОЗИЦИИ: i-я корзина текущего
 *  периода против i-й корзины сравнения; у более короткого периода хвост без точек (null).
 *  У недели/месяца крайние корзины обрезаны границами периода — у каждой корзины число её дней
 *  внутри своего периода (`curDays`/`cmpDays`); пара разной длины — Δ не сравнима (`unevenPair`).
 *  Значения — ПОКАЗАННЫЕ (`shown`: расход положительный), итоги — в знаке витрины, чтобы
 *  считать ячейки тем же `pnlCompactCells`, что и строка таблицы. */
import type { Grain, OzTrend, TrendBucket } from "@/api/ozonAnalytics";
import { REV_EPS, share, shown, type Kind } from "./calc";
import { bucketLabel, fmtDate } from "./format";
import { addDays, daysInMonth, rangeDays, type Range } from "./period";

/** Шаг по умолчанию: день — до 62 дней, неделя — до 370, дальше — месяц. */
export const DAY_MAX_DAYS = 62;
export const WEEK_MAX_DAYS = 370;

export function defaultGrain(r: Range): Grain {
  const days = rangeDays(r);
  if (days <= DAY_MAX_DAYS) return "day";
  if (days <= WEEK_MAX_DAYS) return "week";
  return "month";
}

/** Суммы группы по корзинам оси (в знаке витрины): пустая корзина — 0.
 *  Корзина ответа вне оси или без `by_grp[grp]` — расхождение с контрактом бэка, ошибка. */
export function axisValues(
  buckets: readonly TrendBucket[],
  axis: readonly string[],
  grp: string,
): number[] {
  return axisBy(buckets, axis, (b) => {
    const v = b.by_grp[grp];
    if (v === undefined) {
      throw new Error(`тренд: в корзине ${b.bucket} нет суммы группы «${grp}»`);
    }
    return v;
  });
}

/** Выручка корзин по оси (ответ `/trend` БЕЗ среза, поле `rev`): пустая корзина — 0. */
export function axisRevenue(buckets: readonly TrendBucket[], axis: readonly string[]): number[] {
  return axisBy(buckets, axis, (b) => b.rev);
}

function axisBy(
  buckets: readonly TrendBucket[],
  axis: readonly string[],
  pick: (b: TrendBucket) => number,
): number[] {
  const byBucket = new Map<string, number>();
  for (const b of buckets) byBucket.set(b.bucket, pick(b));
  const axisSet = new Set(axis);
  const outside = [...byBucket.keys()].filter((k) => !axisSet.has(k));
  if (outside.length) {
    throw new Error(`тренд: корзины вне оси периода: ${outside.join(", ")}`);
  }
  return axis.map((k) => byBucket.get(k) ?? 0);
}

export interface ItemTrendPoint {
  /** порядковый номер корзины периода (ось X) */
  i: number;
  /** подпись оси: дата корзины текущего периода (за его концом — корзины сравнения) */
  label: string;
  curBucket: string | null;
  cmpBucket: string | null;
  /** показанное значение; null — корзины в этом периоде нет (хвост короткого периода) */
  cur: number | null;
  cmp: number | null;
  /** дней корзины внутри своего периода (корзина ∩ период); null — корзины нет */
  curDays: number | null;
  cmpDays: number | null;
}

export interface ItemTrendSeries {
  points: ItemTrendPoint[];
  /** суммы периодов в знаке витрины (как `cur`/`cmp` строки сводки); cmp null — без сравнения */
  totalCur: number;
  totalCmp: number | null;
}

const round2 = (x: number): number => Math.round(x * 100) / 100;
/** Показанное значение без «−0» (расход 0 → −0 после смены знака). */
const shownAt = (x: number, kind: Kind): number => {
  const v = shown(x, kind);
  return v === 0 ? 0 : v;
};
const sum = (xs: number[]): number => round2(xs.reduce((a, b) => a + b, 0));

/** Последний день корзины, начинающейся в `bucket` (неделя — с понедельника, как на бэке). */
function bucketEnd(bucket: string, grain: Grain): string {
  if (grain === "day") return bucket;
  if (grain === "week") return addDays(bucket, 6);
  const [y, m] = bucket.split("-").map(Number) as [number, number];
  return `${bucket.slice(0, 8)}${String(daysInMonth(y, m)).padStart(2, "0")}`;
}

/** Дней корзины внутри периода: корзина ∩ [from, to]. Пустое пересечение — корзина не из этого
 *  периода, расхождение с контрактом бэка — ошибка. */
export function bucketDays(bucket: string, grain: Grain, r: Range): number {
  const from = bucket > r.from ? bucket : r.from;
  const end = bucketEnd(bucket, grain);
  const to = end < r.to ? end : r.to;
  if (from > to) {
    throw new Error(`тренд: корзина ${bucket} вне периода ${r.from}…${r.to}`);
  }
  return rangeDays({ from, to });
}

/** Пара корзин разной длины (неполная крайняя неделя/месяц): «неполная корзина: 4 дн. против 7».
 *  null — длины равны или одной из корзин нет. */
export function unevenPair(p: Pick<ItemTrendPoint, "curDays" | "cmpDays">): string | null {
  if (p.curDays === null || p.cmpDays === null || p.curDays === p.cmpDays) return null;
  return `неполная корзина: ${p.curDays} дн. против ${p.cmpDays}`;
}

/** Ряд окна из ответа `/trend` со срезом `grp` (+ `item`); `period`/`cmpPeriod` — периоды
 *  запроса (для длины крайних корзин). */
export function itemTrendSeries(
  data: OzTrend,
  grp: string,
  kind: Kind,
  period: Range,
  cmpPeriod: Range | null,
): ItemTrendSeries {
  const curAxis = data.buckets_cur;
  const cmpAxis = data.buckets_cmp;
  const curVals = axisValues(data.cur, curAxis, grp);
  if ((data.cmp === null) !== (cmpAxis === null)) {
    throw new Error("тренд: cmp и buckets_cmp должны быть заданы вместе");
  }
  if ((cmpAxis === null) !== (cmpPeriod === null)) {
    throw new Error("тренд: сравнение в ответе не соответствует периоду сравнения запроса");
  }
  const cmpVals = data.cmp && cmpAxis ? axisValues(data.cmp, cmpAxis, grp) : null;
  const n = Math.max(curAxis.length, cmpAxis?.length ?? 0);
  const points: ItemTrendPoint[] = [];
  for (let i = 0; i < n; i++) {
    const curBucket = curAxis[i] ?? null;
    const cmpBucket = cmpAxis?.[i] ?? null;
    const labelBucket = (curBucket ?? cmpBucket)!;
    points.push({
      i,
      label: bucketLabel(labelBucket, data.grain),
      curBucket,
      cmpBucket,
      cur: curBucket === null ? null : shownAt(curVals[i]!, kind),
      cmp: cmpBucket === null || !cmpVals ? null : shownAt(cmpVals[i]!, kind),
      curDays: curBucket === null ? null : bucketDays(curBucket, data.grain, period),
      cmpDays: cmpBucket === null ? null : bucketDays(cmpBucket, data.grain, cmpPeriod!),
    });
  }
  return {
    points,
    totalCur: sum(curVals),
    totalCmp: cmpVals ? sum(cmpVals) : null,
  };
}

/** «01.10–17.10.2026», через год — «15.12.2025–14.01.2026». */
export function rangeLabel(r: Range): string {
  const from = fmtDate(r.from);
  const to = fmtDate(r.to);
  return r.from.slice(0, 4) === r.to.slice(0, 4) ? `${from.slice(0, 5)}–${to}` : `${from}–${to}`;
}

/** Подпись корзины в тултипе: день — дата, неделя — «с 07.09.2026», месяц — «сен 2026». */
export function bucketTip(bucket: string, grain: Grain): string {
  if (grain === "month") return bucketLabel(bucket, grain);
  return grain === "week" ? `нед. с ${fmtDate(bucket)}` : fmtDate(bucket);
}

/** Точка ряда «% выручки»: `cur`/`cmp` — доля выручки своей корзины (0.123 = 12,3 %), со знаком
 *  показанного числа (компенсация расходов — с минусом). null — корзины нет (хвост короткого
 *  периода) ИЛИ у корзины нет выручки (≤ `REV_EPS`): % не определён, на графике разрыв, а не 0. */
export interface ItemTrendPctPoint extends ItemTrendPoint {
  /** показанная сумма статьи корзины, ₽; null — корзины нет */
  curRub: number | null;
  cmpRub: number | null;
  /** выручка корзины, ₽; null — корзины нет */
  curRev: number | null;
  cmpRev: number | null;
}

export interface ItemTrendPctSeries {
  points: ItemTrendPctPoint[];
  /** Σ выручки окна (сверяется с выручкой сводки); cmp null — без сравнения */
  revCur: number;
  revCmp: number | null;
  /** итог окна: Σ статьи ÷ Σ выручки (не среднее процентов); null — выручки нет */
  shareCur: number | null;
  shareCmp: number | null;
  /** корзин без выручки (≈0) — % не определён (разрыв линии) */
  noRevCur: number;
  noRevCmp: number;
  /** корзин с ОТРИЦАТЕЛЬНОЙ выручкой (возвраты больше продаж) — % тоже не определён */
  negRevCur: number;
  negRevCmp: number;
}

/** Причина, по которой у корзины нет %: выручка ≈0 или отрицательная; null — % определён. */
export function noPctReason(rev: number | null): "zero" | "negative" | null {
  if (rev === null || rev > REV_EPS) return null;
  return rev < -REV_EPS ? "negative" : "zero";
}

/** Корзина есть, а % нет — у неё нет выручки. */
export function noRevenue(bucket: string | null, value: number | null): boolean {
  return bucket !== null && value === null;
}

const sameAxis = (a: readonly string[], b: readonly string[]): boolean =>
  a.length === b.length && a.every((x, i) => x === b[i]);

/** Ряд «% выручки» из рублёвого ряда окна и выручки корзин (`/trend` без среза, тот же период,
 *  шаг и сравнение). Корзины сопоставляются по тем же осям: i-я корзина статьи — i-я корзина
 *  выручки своего периода; оси не совпали — ошибка контракта, а не молчаливый сдвиг. */
export function itemTrendPctSeries(
  series: ItemTrendSeries,
  revenue: OzTrend,
  kind: Kind,
): ItemTrendPctSeries {
  if (revenue.grp !== null || revenue.item !== null) {
    throw new Error("тренд выручки: ответ со срезом, нужна выручка без среза");
  }
  const curAxis = series.points.flatMap((p) => (p.curBucket === null ? [] : [p.curBucket]));
  const cmpAxis =
    series.totalCmp === null
      ? null
      : series.points.flatMap((p) => (p.cmpBucket === null ? [] : [p.cmpBucket]));
  if ((revenue.cmp === null) !== (revenue.buckets_cmp === null)) {
    throw new Error("тренд выручки: cmp и buckets_cmp должны быть заданы вместе");
  }
  if (!sameAxis(revenue.buckets_cur, curAxis)) {
    throw new Error("тренд выручки: корзины текущего периода не совпадают с графиком статьи");
  }
  if (
    (revenue.buckets_cmp === null) !== (cmpAxis === null) ||
    (cmpAxis !== null && !sameAxis(revenue.buckets_cmp!, cmpAxis))
  ) {
    throw new Error("тренд выручки: корзины периода сравнения не совпадают с графиком статьи");
  }
  const revCurVals = axisRevenue(revenue.cur, curAxis);
  const revCmpVals = revenue.cmp && cmpAxis ? axisRevenue(revenue.cmp, cmpAxis) : null;
  let noRevCur = 0;
  let noRevCmp = 0;
  let negRevCur = 0;
  let negRevCmp = 0;
  const points = series.points.map((p, i): ItemTrendPctPoint => {
    const curRev = p.curBucket === null ? null : revCurVals[i]!;
    const cmpRev = p.cmpBucket === null || !revCmpVals ? null : revCmpVals[i]!;
    const cur = p.cur === null || curRev === null ? null : share(p.cur, curRev);
    const cmp = p.cmp === null || cmpRev === null ? null : share(p.cmp, cmpRev);
    if (noRevenue(p.curBucket, cur)) {
      if (noPctReason(curRev) === "negative") negRevCur++;
      else noRevCur++;
    }
    if (noRevenue(p.cmpBucket, cmp)) {
      if (noPctReason(cmpRev) === "negative") negRevCmp++;
      else noRevCmp++;
    }
    return { ...p, cur, cmp, curRub: p.cur, cmpRub: p.cmp, curRev, cmpRev };
  });
  const revCur = sum(revCurVals);
  const revCmp = revCmpVals ? sum(revCmpVals) : null;
  return {
    points,
    revCur,
    revCmp,
    shareCur: share(shownAt(series.totalCur, kind), revCur),
    shareCmp:
      series.totalCmp === null || revCmp === null
        ? null
        : share(shownAt(series.totalCmp, kind), revCmp),
    noRevCur,
    noRevCmp,
    negRevCur,
    negRevCmp,
  };
}
